import React, { FC, useEffect, useState } from "react";
import { MyInput } from "pages/StepPages/StepTwo/MyInput/MyInput";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { MySelect } from "pages/StepPages/StepTwo/MySelect/MySelect";
import { ILocation, IObject } from "toolkit/steps/stepSeven/stepSeven.types";
import Switch from "components-new/Switch";
import { ReactComponent as FileIcon } from "images/file.svg";
import {
	assignAddressNumber,
	assignNumber,
	dowloadDocument,
	uploadDocument,
} from "toolkit/steps/stepSeven/stepSeven.thunks";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "toolkit/hooks";
import styles from "./AssigningNumber.module.sass";
import {objectsKiiAPI} from "libs/apiClient/ObjectsKii";

interface AssigningNumberProps {
	close: () => void;
	object: IObject;
	address: ILocation | null;
}

export const AssigningNumber: FC<AssigningNumberProps> = ({ close, object, address }) => {
	const [submitError, setSubmitError] = useState<boolean>(false);
	const { organizationId } = useParams();
	const [registrationNumber, setRegistrationNumber] = useState<string>("");
	const [newDocument, setNewDocument] = useState<any>(null);
	const [selectedCategory, setSelectedCategory] = useState<{
		id?: number | null;
		name: string;
	}>({
		id: null,
		name: "",
	});
	const [categoryChangeSwitch, setCategoryChangeSwitch] = useState<boolean>(false);
	const dispatch = useAppDispatch();

	const categoriesList = [
		{ id: 99, name: "Не присвоена" },
		{ id: 1, name: "1" },
		{ id: 2, name: "2" },
		{ id: 3, name: "3" },
	];

	const categoriesObject = {
		99: "Не присвоена",
		1: "1",
		2: "2",
		3: "3",
	};

	useEffect(() => {
		if (!address && object) {
			if (object.fstekId) setRegistrationNumber(String(object.fstekId));
			else if (object.lastFstekId) setRegistrationNumber(String(object.lastFstekId));

			if (object.fstekCategory) {
				setSelectedCategory({
					id: object.fstekCategory,
					name: categoriesObject[object.fstekCategory as keyof typeof categoriesObject],
				});
			} else {
				setSelectedCategory({
					id: object.fstekCategory,
					name: categoriesObject[object.assignedCategory as keyof typeof categoriesObject],
				});
			}
		} else {
			if (address?.fstekId) setRegistrationNumber(String(address.fstekId));
		}
		//eslint-disable-next-line
	}, []);

	const checkNumber = (): boolean => {
		const length = String(object.fstekId).length < 5 ? String(object.fstekId).length : 5;
		if (registrationNumber.slice(0, length) !== String(object.fstekId).slice(0, length)) return false;
		else return true;
	};

	const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
		if (!organizationId) return;
		e.preventDefault();
		if (!registrationNumber.trim() || (address && !checkNumber())) {
			setSubmitError(true);
		} else {
			setSubmitError(false);
			if (object && !address) {
				// присвоение без доков false false => true (success) (search)
				// ! присвоение с первичным доком true false => false (success) (search)
				// присвоение со старым доком false true => true (success) (search)
				// присвоение со старым доком и новым true true => true (success)

				dispatch(
					assignNumber({
						organizationId: +organizationId,
						objectId: object.id,
						registrationNumber: registrationNumber,
						selectedCategory: selectedCategory.id,
						handler: Boolean(
							(!newDocument?.name && object.fstekLetter) ||
								(!newDocument?.name && !object.fstekLetter) ||
								(newDocument?.name && object.fstekLetter),
						),
						updateRequired: Boolean(newDocument?.name && object.fstekLetter),
						file: (Boolean(newDocument?.name && object.fstekLetter) && newDocument) || null,
					}),
				);
				if (newDocument?.name && !object.fstekLetter) {
					dispatch(
						uploadDocument({
							organizationId: +organizationId,
							file: newDocument,
							allCheckedGlobal: false,
							attachmentList: [object.id],
							excludedObjects: [],
							filter: true,
							handler: true,
							reload: true,
						}),
					);
				}
			} else {
				if (registrationNumber.trim() !== String(address?.fstekId).trim() && address)
					dispatch(
						assignAddressNumber({
							orgId: Number(organizationId),
							object,
							addressId: address.id,
							registryId: registrationNumber,
						}),
					);
			}

			close();
		}
	};

	const changeHandler = () => {
		if (categoryChangeSwitch) {
			setCategoryChangeSwitch(false);
		} else {
			setCategoryChangeSwitch(true);
		}
	};

	const documentHandler = (files: FileList | null) => {
		if (files?.length) setNewDocument(files![0]);
	};

	const downloadingHandler = () => {
		if (!organizationId || newDocument || !object) return;
		objectsKiiAPI.exportedTouchDetail(+organizationId,object.id,{action:'DOWNLOAD'})
		dispatch(
			dowloadDocument({
				organizationId: +organizationId,
				name: object.fstekLetter!.name,
				documentKey: object.fstekLetter!.key,
			}),
		);
	};

	return (
		<section className={styles.wrp}>
			<div className={styles.header}>
				<h4 className={styles.title}>Присвоение номера</h4>
				<span className={styles.subtitle}>Введите номер, присвоенный объекту в ФСТЭК</span>
			</div>
			<form onSubmit={submitHandler} className={styles.form}>
				<div className={styles["form-wrp"]}>
					<MyInput
						className={styles.item}
						required={true}
						condition={
							(submitError && !registrationNumber.trim()) || (Boolean(address) && submitError && !checkNumber())
						}
						name="Введите реестровый номер"
						placeholder={"1517745555"}
						value={registrationNumber}
						set={(e) => {
							setRegistrationNumber(e.target.value);
						}}
						warning={{ text: "Данные не совпадают", condition: Boolean(address) && submitError && !checkNumber() }}
					/>
					{object && !address && (
						<>
							<Switch className={styles.switch} checked={categoryChangeSwitch} onChange={changeHandler}>
								<span className={styles["switch-txt"]}>Изменить категорию</span>
							</Switch>
							<MySelect
								className={styles.item}
								list={categoriesList}
								value={selectedCategory.name}
								set={(selected) => {
									setSelectedCategory(selected);
								}}
								name="Изменить категорию значимости"
								placeholder="Выберите категорию"
								width="full"
								required={true}
								error={submitError}
								scroll={false}
								disabled={!categoryChangeSwitch}
								search={false}
							/>
							<div className={styles.item}>
								<div className={styles["document-wrp"]}>
									{newDocument?.name || object.fstekLetter?.name ? (
										<div onClick={downloadingHandler} className={styles.document}>
											<div>
												<FileIcon className={styles["document-icon"]} />
											</div>
											<span className={styles["document-name"]}>{newDocument?.name || object.fstekLetter?.name}</span>
										</div>
									) : (
										<div></div>
									)}
									<label className={styles["document-change"]} htmlFor={`doc-${object.id}`}>
										{newDocument?.name || object.fstekLetter?.name ? "Изменить" : "Прикрепить документ"}
									</label>
									<input
										onChange={(e) => documentHandler(e.target.files)}
										type="file"
										id={`doc-${object.id}`}
										className="none"
										accept=".docx, .pdf, .rtf"
									/>
								</div>
							</div>
						</>
					)}
				</div>
				<div className={styles.btns}>
					<Button onClick={close} type="button" variant={BUTTON_VARIANTS.OUTLINED} className={styles.button}>
						Отменить
					</Button>
					<Button type="submit" className={styles.button}>
						Готово
					</Button>
				</div>
			</form>
		</section>
	);
};
